// Logica del configuratore "Profili su misura" (prezzo, codice, decodifica codice).
// Copiata senza modifiche da Configurator.js (versione prima del redesign 2026-10):
// codici e listini devono restare identici a quelli già usati negli ordini.

export const toNumber = (val) => {
  const n = Number(String(val ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

export const roundCurrency = (val) => Math.round((toNumber(val) + Number.EPSILON) * 100) / 100;
export const formatCurrency = (val) => roundCurrency(val).toFixed(2);
export const formatLedLabel = (ledColor) => (/^\d+$/.test(String(ledColor || '')) ? `${ledColor}K` : String(ledColor || ''));

export const parsePowerW = (p) => {
  if (!p) return NaN;
  const n = parseFloat(String(p).replace(',', '.').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

export const getPowerWattsPerMeter = (power) => {
  const matches = String(power || '').match(/\d+(?:[.,]\d+)?/g);
  if (!matches) return 0;
  const total = matches.reduce((sum, raw) => sum + toNumber(raw), 0);
  return Math.round(total);
};

export const getPowerPriceKey = (power, voltage, ledColor) => {
  const watts = getPowerWattsPerMeter(power);
  if (ledColor === 'CCT') {
    return `CCT_${watts}_${voltage}`;
  }
  return `${watts}_${voltage}`;
};

const getProfileMaterialPricePerMeter = (configData, model, profileColor) => {
  const finishOverride = configData.modelFinishPricesPerMeter?.[model]?.[profileColor];
  if (finishOverride != null) {
    return toNumber(finishOverride);
  }

  return (
    toNumber(configData.modelPricesPerMeter?.[model]) +
    toNumber(configData.finishingPricePerMeter?.[profileColor])
  );
};

const getProfileClipsPrice = (configData, model, lengthMeters) => {
  const unit = toNumber(configData.clipsPrices?.[model]);
  if (configData.clipsPriceMode?.[model] === 'fixedPair') {
    return unit * 2;
  }
  return unit * lengthMeters;
};

export const isPowerSupplyIncluded = (configData, model) =>
  (configData.powerSupplyIncludedModels || []).includes(model);

export const getConfiguredTotalPowerW = ({ power, length, emissionOption }) => {
  const multiplier = emissionOption === 'biemissione' ? 2 : 1;
  return Math.round(getPowerWattsPerMeter(power) * (length / 100) * multiplier);
};

// Calcolo unico del listino (prezzo unitario) usato sia nel riepilogo che nel carrello
export const computeListPrice = (configData, {
  model,
  profileColor,
  length,          // cm
  ledColor,
  power,           // es. "14W/m"
  voltage,         // "24V" | "230Vac"
  cover,
  cableLength,
  powerSupply,
  dimmerTouch,
  emissionOption,
}) => {
  const lengthMeters = length / 100;
  const priceKey = getPowerPriceKey(power, voltage, ledColor);

  const profileMeterPrice = getProfileMaterialPricePerMeter(configData, model, profileColor);
  let powerPrice = toNumber(configData.powerPricesPerMeter?.[priceKey]) * lengthMeters;
  let coverPrice = toNumber(configData.coverPrices?.[cover]) * lengthMeters;
  const wasteThreshold = configData.waste?.thresholdCm ?? Infinity;
  const wasteFactor = length >= wasteThreshold ? (configData.waste?.factor ?? 1) : 1;
  const profilePrice = profileMeterPrice * lengthMeters * wasteFactor;
  // Manodopera: in biemissione il lavoro è maggiore (due strip, due cover) → fattore da config (es. 1.5 = +50%).
  const assemblyFactor =
    emissionOption === 'biemissione' ? toNumber(configData.biEmissionAssemblyFactor) || 1 : 1;
  const assemblyCost = toNumber(configData.assemblyCostPerMeter) * lengthMeters * assemblyFactor;
  const fixedAssemblyCost = toNumber(configData.fixedAssemblyCost) * assemblyFactor;
  const cablePrice = toNumber(configData.cablePrices?.[cableLength]);
  const tappiPrice = toNumber(configData.tappiPrices?.[model]) * 2;
  const clipsPrice = getProfileClipsPrice(configData, model, lengthMeters);
  // Componenti interni sempre inclusi, a metro (es. BMN: piattina dissipatore 25x2 grezza).
  const extraPrice = toNumber(configData.modelExtraCostsPerMeter?.[model]) * lengthMeters;

  if (emissionOption === 'biemissione') {
    powerPrice *= 2;
    // Alcuni profili montano una cover diversa sul secondo lato (es. BMN: copertura alta).
    const secondCoverPerMeter = configData.modelBiEmissionCoverPrices?.[model];
    coverPrice += secondCoverPerMeter != null ? toNumber(secondCoverPerMeter) * lengthMeters : coverPrice;
  }

  let total =
    profilePrice +
    powerPrice +
    coverPrice +
    assemblyCost +
    fixedAssemblyCost +
    cablePrice +
    tappiPrice +
    clipsPrice +
    extraPrice;

  // Alimentatore: aggiungi solo se stai usando un profilo/strip a 24V con alimentazione 230Vac esterna.
  if (powerSupply === '230Vac' && voltage !== '230Vac' && !isPowerSupplyIncluded(configData, model)) {
    total += toNumber(configData.powerSupplyPrice);
  }
  if (dimmerTouch === 'si') {
    total += toNumber(configData.dimmerTouchPrices);
  }

  const listinoUnit = Math.round(total * 4.5);
  return { listinoUnit, priceKey };
};

export const getSuggestedPowerSupply = (totalPowerW, alimentatori) => {
  // +15% margine
  const required = totalPowerW * 1.15;

  const candidates = (Array.isArray(alimentatori) ? alimentatori : [])
    .filter(a => a && String(a.category).toLowerCase() === '24v')
    .map(a => ({ ...a, _powerW: parsePowerW(a.power) }))
    .filter(a => Number.isFinite(a._powerW) && a._powerW >= required)
    .sort((a, b) => a._powerW - b._powerW);

  return candidates[0] || null;
};

// Opzioni per singolo profilo (es. BATTISCOPA): se il modello ha un override in config.json
// si usano solo i suoi colori LED / potenze / lunghezza massima, altrimenti le liste globali.
export const getModelLedColors = (configData, modelName) =>
  configData.modelLedColors?.[modelName] || configData.ledColors || ['2700', '3000', '4000', '5000', 'RGB', 'RGBW', 'CCT'];
export const getModelPowerOptions = (configData, modelName, ledColor) =>
  configData.modelLedPowerOptions?.[modelName]?.[ledColor] || configData.ledPowerOptions?.[ledColor] || [];
export const getModelMaxLength = (configData, modelName) => configData.modelMaxLengthCm?.[modelName] || 500;
// modelCodes: prefisso esplicito quando il nome non dà un codice sensato (es. modelli senza cifre nel nome).
export const getModelCode = (configData, modelName) =>
  configData.modelCodes?.[modelName] ||
  modelName.replace(/\D/g, '') ||
  modelName.replace(/\s+/g, '').toUpperCase();

export const getEmissionCode = (emissionOption) => (emissionOption === 'biemissione' ? 'B' : 'M');
const getEmissionOptionByCode = (emissionCode) => {
  if (emissionCode === 'B') return 'biemissione';
  if (emissionCode === 'M') return 'monoemissione';
  return null;
};

export const getCoverCode = (cover) => {
  let coverCode = String(cover || '').substring(0, 2).toUpperCase();
  if (coverCode === 'SA') {
    coverCode = '8';
  }
  return coverCode;
};

export const generateCode = (configData, model, profileColor, length, ledColor, power, cover, cableLength, dimmerTouch, powerSupply = '24V', emissionOption = 'monoemissione') => {
  const modelCode = getModelCode(configData, model);
  const colorCode = configData.profileColorCodes[profileColor];
  const lengthCode = String(Math.round(length / 5)).padStart(2, '0');
  const ledCode = configData.ledColorCodes[ledColor];
  const emissionCode = getEmissionCode(emissionOption);
  const powerCode = String(getPowerWattsPerMeter(power)).padStart(2, '0');
  let coverCode = cover.substring(0, 2).toUpperCase();

  if (coverCode === "SA") {
    coverCode = "8";
  }

  const cableCode = cableLength;
  const dimmerCode = dimmerTouch === 'si' ? 'D' : 'N';
  const powerSupplyCode = powerSupply === '230Vac' ? 'H' : 'L';

  return `${modelCode}${lengthCode}${ledCode}${coverCode}${colorCode}${emissionCode}${powerCode}${cableCode}${dimmerCode}${powerSupplyCode}`;
};

export const isValidLength = (configData, modelName, length) => {
  const maxLength = getModelMaxLength(configData, modelName);
  return !(length < 30 || length > maxLength || length % 5 !== 0);
};

const getSingleValueByCode = (map, code) =>
  Object.entries(map || {}).find(([, value]) => String(value).toUpperCase() === code)?.[0] || null;

// "Esplodi codice": stessa decodifica di prima, sul profilo aperto
export const decodeProfileCode = (configData, selectedModel, rawCode) => {
  if (!selectedModel) {
    return { error: 'Seleziona prima il profilo corretto.' };
  }

  const code = String(rawCode || '').trim().toUpperCase().replace(/[\s-]/g, '');
  const modelCode = getModelCode(configData, selectedModel.name);
  if (!code.startsWith(modelCode)) {
    return { error: `Questo codice non appartiene al profilo ${selectedModel.name}.` };
  }

  const payload = code.slice(modelCode.length);
  if (payload.length < 9) {
    return { error: 'Codice troppo corto o incompleto.' };
  }

  const lengthSteps = Number(payload.slice(0, 2));
  const length = lengthSteps * 5;
  const ledCode = payload.slice(2, 3);
  const ledColor = getSingleValueByCode(configData.ledColorCodes, ledCode);
  if (!Number.isFinite(lengthSteps) || !isValidLength(configData, selectedModel.name, length)) {
    return { error: 'Lunghezza non valida nel codice.' };
  }
  if (!ledColor) {
    return { error: 'Colore LED non riconosciuto nel codice.' };
  }

  const compatibleCovers = configData.modelCoverCompatibility?.[selectedModel.name] || [];
  const coversBySpecificity = [...compatibleCovers].sort((a, b) => getCoverCode(b).length - getCoverCode(a).length);
  const cableCodes = Object.keys(configData.cablePrices || {}).sort((a, b) => b.length - a.length);

  for (const cover of coversBySpecificity) {
    const coverCode = getCoverCode(cover);
    const afterLed = payload.slice(3);
    if (!afterLed.startsWith(coverCode)) continue;

    const colorIndex = 3 + coverCode.length;
    const colorCode = payload.slice(colorIndex, colorIndex + 1);
    const profileColor = getSingleValueByCode(configData.profileColorCodes, colorCode);
    if (!profileColor || !selectedModel.availableFinishes?.includes(profileColor)) continue;

    const possibleEmissionCode = payload.slice(colorIndex + 1, colorIndex + 2);
    const emissionOption = getEmissionOptionByCode(possibleEmissionCode);
    if (emissionOption === 'biemissione' && !selectedModel.hasEmissionOption) continue;
    const powerIndex = colorIndex + 1 + (emissionOption ? 1 : 0);
    const powerCode = payload.slice(powerIndex, powerIndex + 2);
    const suffix = payload.slice(powerIndex + 2);
    if (suffix.length < 3) continue;

    const dimmerCode = suffix.slice(-2, -1);
    const powerSupplyCode = suffix.slice(-1);
    const cableCode = suffix.slice(0, -2);
    if (!cableCodes.includes(cableCode)) continue;
    if (!['D', 'N'].includes(dimmerCode) || !['H', 'L'].includes(powerSupplyCode)) continue;

    const powerOptions = getModelPowerOptions(configData, selectedModel.name, ledColor);
    const wattCode = Number(powerCode);
    const matchingPowers = powerOptions.filter((option) => getPowerWattsPerMeter(option.power) === wattCode);
    if (matchingPowers.length === 0) continue;

    const preferredVoltage = powerSupplyCode === 'H' ? '230Vac' : '24V';
    const powerOption =
      matchingPowers.find((option) => option.voltage === preferredVoltage) ||
      matchingPowers.find((option) => option.voltage === '24V') ||
      matchingPowers[0];

    return {
      value: {
        profileColor,
        length,
        ledColor,
        powerOption,
        cover,
        cableLength: cableCode,
        dimmerTouch: dimmerCode === 'D' ? 'si' : 'no',
        powerSupply: powerOption.voltage === '230Vac' || powerSupplyCode === 'H' ? '230Vac' : '24V',
        emissionOption: emissionOption || 'monoemissione',
      },
    };
  }

  return { error: 'Codice non compatibile con le opzioni del profilo aperto.' };
};
